import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {select, Store} from '@ngrx/store';

import {environment} from '../../../environments/environment';
import * as fromRoot from '../../reducers';

import {Observable} from 'rxjs';

import {OrganizationUser} from '../../afb-api/models/organization-user';
import {OrganizationUserStatus} from '../../afb-api/enums/organization-user-status.enum';
import {Simple200Response} from '../../afb-api/models/general';
import {TemperatureUnit} from '../../shared';
import {map} from 'rxjs/internal/operators';
import {OrganizationUserRole} from '../../afb-api/enums/organization-user-role.enum';

@Injectable()
export class UserService {

  constructor(private httpClient: HttpClient,
              private store: Store<fromRoot.State>) {
  }

  changeTempUnit(orgToken: string, tempUnit: TemperatureUnit): Observable<Simple200Response> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${orgToken}`,
      'Content-Type': 'application/json'
    });
    const url = `${environment.afbV2Host}org-users/self`;

    return this.httpClient
      .patch<Simple200Response>(url, {tempUnit}, {headers});
  }

  /**
   * Organization Invitations
   */
  inviteToOrg(orgToken: string, email: string, role: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({'Authorization': `Bearer ${orgToken}`});
    const url = `${environment.afbV2Host}orgs/self/invitations`;
    const body = {
      email,
      role
    };

    return this.httpClient
      .post<Simple200Response>(url, body, {headers});
  }

  removeFromOrg(orgToken: string, userId: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({'Authorization': `Bearer ${orgToken}`});
    const url = `${environment.afbV2Host}org-users/${userId}`;

    return this.httpClient
      .delete<Simple200Response>(url, {headers});
  }

  /**
   * Group Invitations
   */
  inviteToGroup(orgToken: string, email: string, role: string, groupId: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({'Authorization': `Bearer ${orgToken}`});
    const url = `${environment.afbV2Host}org-users/self/groups/${groupId}/invitations`;

    const body = {
      email,
      role
    };

    return this.httpClient
      .post<Simple200Response>(url, body, {headers});
  }

  removeFromGroup(orgToken: string, groupId: string, userId: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({'Authorization': `Bearer ${orgToken}`});
    const url = `${environment.afbV2Host}org-users/self/groups/${groupId}/users/${userId}`;

    return this.httpClient
      .delete<Simple200Response>(url, {headers});
  }

  /**
   * Organization users
   */
  getOrgOwner(): Observable<OrganizationUser> {
    return this.store.pipe(
      select(fromRoot.getOrgUserList),
      map((orgUserList: OrganizationUser[]) =>
        orgUserList.find((orgUser: OrganizationUser) => orgUser.role === OrganizationUserRole.Owner)
      )
    );
  }

  getOrgUserList(): Observable<OrganizationUser[]> {
    return this.store.pipe(
      select(fromRoot.getOrgUserList)
    );
  }

  getActiveOrgAdminList(): Observable<OrganizationUser[]> {
    return this.store.pipe(
      select(fromRoot.getOrgUserList),
      map((orgUserList: OrganizationUser[]) => orgUserList.filter((orgUser: OrganizationUser) =>
        orgUser.role === OrganizationUserRole.Admin && orgUser.status === OrganizationUserStatus.Accepted)
      ));
  }

  getPendingOrgAdminList(): Observable<OrganizationUser[]> {
    return this.store.pipe(
      select(fromRoot.getOrgUserList),
      map((orgUserList: OrganizationUser[]) => orgUserList.filter((orgUser: OrganizationUser) =>
        orgUser.role === OrganizationUserRole.Admin && orgUser.status === OrganizationUserStatus.Pending)
      ));
  }

  getActiveOrgMemberList(): Observable<OrganizationUser[]> {
    return this.store.pipe(
      select(fromRoot.getOrgUserList),
      map((orgUserList: OrganizationUser[]) => orgUserList.filter((orgUser: OrganizationUser) =>
        orgUser.role === OrganizationUserRole.Member && orgUser.status === OrganizationUserStatus.Accepted)
      ));
  }

  getPendingOrgMemberList(): Observable<OrganizationUser[]> {
    return this.store.pipe(
      select(fromRoot.getOrgUserList),
      map((orgUserList: OrganizationUser[]) => orgUserList.filter((orgUser: OrganizationUser) =>
        orgUser.role === OrganizationUserRole.Member && orgUser.status === OrganizationUserStatus.Pending)
      ));
  }
}
